import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  X, CheckCircle2, User, Home, MapPin, Tag, IndianRupee,
  FileText, Upload, MessageCircle, Loader2, Video, Trash2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AddressAutocomplete } from './AddressAutocomplete';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from './ui/select';
import { INDIAN_STATES, getCitiesByState, primaryUnitOf, unitsForState } from '../constants/locations';
import { AREA_UNIT_GROUPS, AREA_UNITS } from '../constants/areaUnits';

/** Resolve the best matching AREA_UNITS key for a state's primary unit.
 *  Some backend units are namespaced (e.g. "bigha_bengal") while `stateUnits`
 *  uses bare keys ("bigha"). We prefer an exact-bare match, then a prefixed
 *  match, so setting `area_unit` from the state's primary works either way. */
const resolveAreaUnitForState = (state) => {
  if (!state) return null;
  const primary = primaryUnitOf(state);
  const bareMatch = AREA_UNITS.find((u) => u.value === primary);
  if (bareMatch) return bareMatch.value;
  const prefixMatch = AREA_UNITS.find((u) => u.value.split('_')[0] === primary);
  return prefixMatch?.value || null;
};
import { getWhatsAppLink } from '../constants/contact';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  'Residential',
  'Agricultural',
  'Commercial',
  'Flat Rent',
  'Flat Sell',
  'House Rent',
  'House Sell',
];

const initialForm = {
  submitted_by: 'customer',   // 'agent' | 'customer'
  agent_number: '',
  owner_name: '',
  owner_phone: '',
  owner_email: '',
  owner_address: '',
  title: '',
  property_type: 'Residential',
  listing_type: 'Sell',
  state: '',
  city: '',
  location: '',
  area: '',
  area_unit: 'sqft',
  price: '',
  price_per_bigha: '',
  price_per_katha: '',
  latitude: '',
  longitude: '',
  google_maps_url: '',
  description: '',
  has_documents: false,
  notes: '',
};

export const AddPropertyModal = ({ isOpen, onClose, agentNumber }) => {
  const startForm = agentNumber ? { ...initialForm, submitted_by: 'agent', agent_number: agentNumber } : initialForm;
  const [form, setForm] = useState(startForm);
  const [images, setImages] = useState([]); // uploaded URLs
  const [videos, setVideos] = useState([]); // uploaded video URLs
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [pinning, setPinning] = useState(false);
  const [pinStatus, setPinStatus] = useState(null); // { ok:bool, msg:str }

  useEffect(() => {
    if (isOpen) {
      setSuccess(null);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const cities = form.state ? getCitiesByState(form.state) : [];

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - images.length);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    const skipped = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        skipped.push(`${file.name} (not an image)`);
        continue;
      }
      if (file.size > 15 * 1024 * 1024) {
        skipped.push(`${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB — max 15 MB)`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.url) uploaded.push(res.data.url);
      } catch (err) {
        console.error(`[AddPropertyModal] Upload failed for ${file.name}:`, err);
        const msg = err.response?.data?.detail || 'upload failed';
        skipped.push(`${file.name} (${msg})`);
      }
    }
    setImages((prev) => [...prev, ...uploaded]);
    setUploading(false);
    if (skipped.length) {
      alert(`Some photos were not added:\n\n• ${skipped.join('\n• ')}`);
    }
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - videos.length);
    if (!files.length) return;
    setUploadingVideo(true);
    const uploaded = [];
    const skipped = [];
    for (const file of files) {
      if (!file.type.startsWith('video/')) {
        skipped.push(`${file.name} (not a video)`);
        continue;
      }
      if (file.size > 100 * 1024 * 1024) {
        skipped.push(`${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB — max 100 MB)`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
        });
        if (res.data?.url) uploaded.push(res.data.url);
      } catch (err) {
        console.error(`[AddPropertyModal] Video upload failed for ${file.name}:`, err);
        const msg = err.response?.data?.detail || 'upload failed';
        skipped.push(`${file.name} (${msg})`);
      }
    }
    setVideos((prev) => [...prev, ...uploaded]);
    setUploadingVideo(false);
    if (skipped.length) {
      alert(`Some videos were not added:\n\n• ${skipped.join('\n• ')}`);
    }
  };

  const applyCoords = (lat, lng, msg) => {
    setForm((f) => ({
      ...f,
      latitude: Number(lat).toFixed(6),
      longitude: Number(lng).toFixed(6),
    }));
    setPinStatus({ ok: true, msg });
  };

  /** Try browser geolocation first; fall back to IP-based lookup via ipapi.co
   *  so the button never silently fails. Works on desktops without GPS,
   *  denied-permission scenarios, and older iOS Safari WebViews. */
  const pinLocation = () => {
    setPinning(true);
    setPinStatus(null);

    const ipFallback = async (reason) => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        if (data && data.latitude && data.longitude) {
          applyCoords(data.latitude, data.longitude,
            `Approximate location (${data.city || 'IP-based'}) — ${reason}`);
        } else {
          setPinStatus({ ok: false, msg: `Could not detect location. ${reason} You can paste a Google Maps URL instead.` });
        }
      } catch {
        setPinStatus({ ok: false, msg: `Could not detect location. ${reason} You can paste a Google Maps URL instead.` });
      } finally { setPinning(false); }
    };

    if (!navigator.geolocation) {
      ipFallback('Browser geolocation not available.');
      return;
    }
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      ipFallback('HTTPS required for precise GPS on this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        applyCoords(
          pos.coords.latitude,
          pos.coords.longitude,
          `Pinned within ~${Math.round(pos.coords.accuracy || 0)}m`,
        );
        setPinning(false);
      },
      (err) => {
        // 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT
        const map = {
          1: 'Location permission denied — allow it in your browser or paste a Google Maps URL instead.',
          2: 'GPS signal unavailable on this device — trying IP-based fallback…',
          3: 'GPS timed out — trying IP-based fallback…',
        };
        if (err.code === 1) {
          setPinStatus({ ok: false, msg: map[1] });
          setPinning(false);
        } else {
          ipFallback(map[err.code] || err.message || '');
        }
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.owner_phone.replace(/\D/g, '').length < 10) {
      alert('Please enter a valid 10-digit phone number.');
      return;
    }
    if (form.submitted_by === 'agent' && !form.agent_number.trim()) {
      alert('Agent number is mandatory when uploading on behalf of a customer.');
      return;
    }
    if (images.length === 0) {
      alert('At least one property photo is required. Please upload a photo before submitting.');
      return;
    }
    if (videos.length === 0) {
      alert('A walkthrough video is required — it dramatically improves inquiries. Please upload at least one video.');
      return;
    }
    if (!form.area || Number(form.area) <= 0) {
      alert('Please enter the property area.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        area: `${form.area} ${form.area_unit}`,
        owner_phone: form.owner_phone.replace(/\D/g, ''),
        agent_number: form.submitted_by === 'agent' ? form.agent_number.trim().toUpperCase() : null,
        price_per_bigha: form.price_per_bigha ? Number(form.price_per_bigha) : null,
        price_per_katha: form.price_per_katha ? Number(form.price_per_katha) : null,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        google_maps_url: form.google_maps_url.trim() || null,
        images,
        videos,
      };
      const res = await axios.post(`${API}/property-submissions`, payload);
      setSuccess(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not submit your property. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setForm(startForm);
    setImages([]);
    setVideos([]);
    setSuccess(null);
    onClose();
  };

  // Build a WhatsApp message that sends the lead to the company's WhatsApp.
  // Pass the lead message into getWhatsAppLink so it becomes the SOLE ?text param
  // (avoids the double `?text=` bug when concatenating manually).
  const leadMessage = success
    ? `New Property Listing on Acre Mitra\n\n` +
      `*Owner:* ${success.owner_name}\n` +
      `*Phone:* ${success.owner_phone}\n` +
      (success.owner_email ? `*Email:* ${success.owner_email}\n` : '') +
      `\n*Property:* ${success.title}\n` +
      `*Type:* ${success.property_type} (${success.listing_type})\n` +
      `*Location:* ${success.location}${success.city ? ', ' + success.city : ''}${success.state ? ', ' + success.state : ''}\n` +
      `*Area:* ${success.area}\n` +
      `*Price:* ${success.price}\n` +
      (success.description ? `\n*Description:* ${success.description.slice(0, 200)}\n` : '') +
      `\nSubmission ID: ${success.id.slice(0, 8)}`
    : '';

  const whatsAppDirect = success ? getWhatsAppLink(leadMessage) : '#';

  // Success view
  if (success) {
    return createPortal(
      <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-4 animate-popupFade">
        <div className="bg-white rounded-2xl max-w-md w-full p-8 relative max-h-[92vh] overflow-y-auto animate-popupScale">
          <button
            onClick={resetAndClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          >
            <X size={20} />
          </button>
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 size={36} className="text-emerald-700" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Property Submitted!</h2>
            <p className="text-slate-600 mb-5">
              Thanks {success.owner_name}. We&apos;ve received your listing and our team will review it shortly.
            </p>
            <div className="bg-slate-50 rounded-xl p-4 text-left text-sm mb-5">
              <div className="flex justify-between mb-1">
                <span className="text-slate-500">Submission ID</span>
                <span className="font-mono text-slate-700">{success.id.slice(0, 8)}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-slate-500">Title</span>
                <span className="font-medium text-slate-900 truncate ml-2">{success.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Status</span>
                <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">
                  {success.status}
                </span>
              </div>
            </div>

            <p className="text-sm text-slate-700 mb-3">
              ✅ Saved to Acre Mitra. Send a copy to our WhatsApp now to get faster response:
            </p>
            <a
              href={whatsAppDirect}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-am-accent w-full py-3.5 text-base mb-3"
              data-testid="submit-whatsapp-btn"
            >
              <MessageCircle size={18} />
              Send to Acre Mitra WhatsApp
            </a>
            <button
              onClick={resetAndClose}
              className="btn-am-ghost w-full py-3 text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-4 animate-popupFade"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-2xl w-full p-6 sm:p-8 relative max-h-[92vh] overflow-y-auto animate-popupScale"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          data-testid="add-property-close"
        >
          <X size={22} />
        </button>

        <div className="mb-6">
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
            <Home size={22} className="text-emerald-700" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">List Your Property</h2>
          <p className="text-slate-600 text-sm mt-1">
            Sell or rent your land, plot, flat, or house through Acre Mitra. Fill in the details below — our team will get in touch.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Uploaded by (Agent vs Customer) */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Tag size={16} className="text-emerald-700" />
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Uploaded by *</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <label
                className={`cursor-pointer rounded-xl border-2 px-4 py-3 flex items-center gap-3 transition-all ${form.submitted_by === 'customer' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300'}`}
                data-testid="add-prop-by-customer"
              >
                <input
                  type="radio"
                  name="submitted_by"
                  value="customer"
                  checked={form.submitted_by === 'customer'}
                  onChange={() => setForm({ ...form, submitted_by: 'customer', agent_number: '' })}
                  className="w-4 h-4 accent-emerald-600"
                />
                <div>
                  <div className="font-semibold text-slate-900 text-sm">I&apos;m the customer</div>
                  <div className="text-xs text-slate-500">Owner listing on their own behalf.</div>
                </div>
              </label>
              <label
                className={`cursor-pointer rounded-xl border-2 px-4 py-3 flex items-center gap-3 transition-all ${form.submitted_by === 'agent' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300'}`}
                data-testid="add-prop-by-agent"
              >
                <input
                  type="radio"
                  name="submitted_by"
                  value="agent"
                  checked={form.submitted_by === 'agent'}
                  onChange={() => setForm({ ...form, submitted_by: 'agent' })}
                  className="w-4 h-4 accent-emerald-600"
                />
                <div>
                  <div className="font-semibold text-slate-900 text-sm">I&apos;m an Acremitra Agent</div>
                  <div className="text-xs text-slate-500">Verified channel partner — needs agent number.</div>
                </div>
              </label>
            </div>
            {form.submitted_by === 'agent' && (
              <div className="mt-3">
                <Label>Agent Number *</Label>
                <Input
                  required
                  value={form.agent_number}
                  onChange={(e) => setForm({ ...form, agent_number: e.target.value.toUpperCase() })}
                  placeholder="AM-AGT-2026-XXXX"
                  data-testid="add-prop-agent-number"
                />
                <p className="text-xs text-slate-500 mt-1">
                  The unique agent number issued to you when your Channel Partner application was verified.
                </p>
              </div>
            )}
          </div>

          {/* Owner */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <User size={16} className="text-emerald-700" />
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Owner Details</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Full Name *</Label>
                <Input
                  required
                  value={form.owner_name}
                  onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                  data-testid="add-prop-owner-name"
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  required
                  value={form.owner_phone}
                  onChange={(e) => setForm({ ...form, owner_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  placeholder="10-digit mobile"
                  data-testid="add-prop-owner-phone"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.owner_email}
                  onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={form.owner_address}
                  onChange={(e) => setForm({ ...form, owner_address: e.target.value })}
                  placeholder="Your residential address"
                />
              </div>
            </div>
          </div>

          {/* Property */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Tag size={16} className="text-emerald-700" />
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Property Details</h3>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Property Title *</Label>
                <Input
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. 3 BHK Flat near Whitefield"
                  data-testid="add-prop-title"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Property Type *</Label>
                  <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[1100]">
                      {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Listing Type *</Label>
                  <Select value={form.listing_type} onValueChange={(v) => setForm({ ...form, listing_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[1100]">
                      <SelectItem value="Sell">Sell</SelectItem>
                      <SelectItem value="Rent">Rent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>State</Label>
                  <Select value={form.state || 'NONE'} onValueChange={(v) => {
                    const nextState = v === 'NONE' ? '' : v;
                    const nextUnit = resolveAreaUnitForState(nextState);
                    setForm({
                      ...form,
                      state: nextState,
                      city: '',
                      // Auto-pick the state's primary local unit so buyers see
                      // Katha in WB, Guntha in Karnataka, Marla in Punjab etc.
                      // Falls back to sqft when no mapping exists.
                      area_unit: nextUnit || 'sqft',
                    });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent className="z-[1100] max-h-72">
                      <SelectItem value="NONE">— Select State —</SelectItem>
                      {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>City</Label>
                  <Select
                    value={form.city || 'NONE'}
                    onValueChange={(v) => setForm({ ...form, city: v === 'NONE' ? '' : v })}
                    disabled={!form.state}
                  >
                    <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent className="z-[1100] max-h-72">
                      <SelectItem value="NONE">— Select City —</SelectItem>
                      {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><MapPin size={13} className="text-emerald-700" /> Location / Address *</Label>
                <AddressAutocomplete
                  required
                  value={form.location}
                  state={form.state}
                  onChange={(v) => setForm((f) => ({ ...f, location: v }))}
                  onSelect={(r) => {
                    setForm((f) => ({ ...f, location: r.label }));
                    applyCoords(r.lat, r.lng, 'Pinned from the address you picked.');
                  }}
                  placeholder="Start typing — e.g. Salt Lake Sector V"
                  data-testid="add-prop-location"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Area *</Label>
                  <div className="flex gap-2">
                    <Input
                      required
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      className="flex-1"
                      value={form.area}
                      onChange={(e) => setForm({ ...form, area: e.target.value })}
                      placeholder="e.g. 3"
                      data-testid="add-prop-area"
                    />
                    <Select
                      value={form.area_unit}
                      onValueChange={(v) => setForm({ ...form, area_unit: v })}
                    >
                      <SelectTrigger className="w-[160px] shrink-0" data-testid="add-prop-area-unit">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {/* Region-preferred units bubble up first when the seller picks a state.
                            Maps our per-state `unitsForState` catalogue to the AREA_UNITS keys
                            already known to the backend. */}
                        {form.state && (() => {
                          const localKeys = new Set(unitsForState(form.state).map((u) => u.key));
                          const matches = AREA_UNITS.filter((u) => {
                            const bare = u.value.split('_')[0]; // e.g. "bigha_bengal" → "bigha"
                            return localKeys.has(bare) || localKeys.has(u.value);
                          });
                          if (matches.length === 0) return null;
                          return (
                            <SelectGroup>
                              <SelectLabel className="text-[10px] uppercase tracking-widest text-amber-700">
                                🇮🇳 {form.state} preferred
                              </SelectLabel>
                              {matches.map((u) => (
                                <SelectItem key={`pref-${u.value}`} value={u.value}>
                                  {u.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          );
                        })()}
                        {Object.entries(AREA_UNIT_GROUPS).map(([group, units]) => (
                          <SelectGroup key={group}>
                            <SelectLabel className="text-[10px] uppercase tracking-widest text-emerald-700">
                              {group}
                            </SelectLabel>
                            {units.map((u) => (
                              <SelectItem key={u.value} value={u.value}>
                                {u.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Pick the local unit — buyers in your region search by these.
                  </p>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5"><IndianRupee size={13} className="text-emerald-700" /> Expected Price *</Label>
                  <Input
                    required
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="e.g. 45,00,000 or Rs 12 lakh/month"
                    data-testid="add-prop-price"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1.5"><IndianRupee size={13} className="text-emerald-700" /> Price per Bigha (₹)</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={form.price_per_bigha}
                    onChange={(e) => setForm({ ...form, price_per_bigha: e.target.value })}
                    placeholder="e.g. 250000"
                    data-testid="add-prop-per-bigha"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Optional. Helps buyers who compare per-unit rates.</p>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5"><IndianRupee size={13} className="text-emerald-700" /> Price per Katha (₹)</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={form.price_per_katha}
                    onChange={(e) => setForm({ ...form, price_per_katha: e.target.value })}
                    placeholder="e.g. 12500"
                    data-testid="add-prop-per-katha"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Optional. Bengal / Bihar markets often quote per katha.</p>
                </div>
              </div>

              {/* Google Maps location */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-emerald-700" />
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-700">Map location</span>
                  <span className="text-[10px] text-slate-400">Optional — helps buyers find the plot</span>
                </div>
                <div>
                  <Label className="text-xs">Google Maps URL</Label>
                  <Input
                    value={form.google_maps_url}
                    onChange={(e) => setForm({ ...form, google_maps_url: e.target.value })}
                    placeholder="Paste a Google Maps link (e.g. https://maps.app.goo.gl/xxxx)"
                    data-testid="add-prop-maps-url"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={form.latitude}
                      onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                      placeholder="22.5726"
                      data-testid="add-prop-lat"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={form.longitude}
                      onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                      placeholder="88.3639"
                      data-testid="add-prop-lng"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={pinLocation}
                  disabled={pinning}
                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline underline-offset-2 disabled:opacity-50 disabled:cursor-wait inline-flex items-center gap-1.5"
                  data-testid="add-prop-pin-current"
                >
                  {pinning ? (
                    <>
                      <span className="inline-block w-3 h-3 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                      Detecting your location…
                    </>
                  ) : (
                    <>📍 Pin my current location</>
                  )}
                </button>
                {pinStatus && (
                  <div
                    className={`mt-1 text-[11px] rounded-md px-2 py-1.5 border ${
                      pinStatus.ok
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : 'bg-amber-50 border-amber-200 text-amber-900'
                    }`}
                    data-testid="add-prop-pin-status"
                  >
                    {pinStatus.ok ? '✓ ' : '⚠ '}{pinStatus.msg}
                  </div>
                )}
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><FileText size={13} className="text-emerald-700" /> Description</Label>
                <Textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Key features, amenities, surroundings, anything that makes this property special…"
                />
              </div>
            </div>
          </div>

          {/* Images & Docs */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Upload size={16} className="text-emerald-700" />
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Photos, Video & Documents</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="flex items-center gap-1">
                  Upload photos (up to 5) <span className="text-red-600 font-bold">*</span>
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploading || images.length >= 5}
                  className="cursor-pointer mt-1"
                  data-testid="add-prop-image-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  At least 1 photo is required. Serious buyers skip listings without photos.
                </p>
                {uploading && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                    <Loader2 size={14} className="animate-spin" /> Uploading…
                  </div>
                )}
                {images.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 mt-2" data-testid="add-prop-image-thumbs">
                    {images.map((img, i) => (
                      <div key={img || `upload-${i}`} className="relative group">
                        <img
                          src={img.match(/^\/api\/(uploads|files)\//) ? `${BACKEND_URL}${img}` : img}
                          alt={`Property ${i + 1}`}
                          className="w-full h-16 object-cover rounded-md border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))}
                          className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`Remove photo ${i + 1}`}
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label className="flex items-center gap-1">
                  <Video size={13} className="text-emerald-700" />
                  Upload a walkthrough video <span className="text-red-600 font-bold">*</span>
                </Label>
                <Input
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleVideoUpload}
                  disabled={uploadingVideo || videos.length >= 3}
                  className="cursor-pointer mt-1"
                  data-testid="add-prop-video-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Required. A 30-90 sec walkthrough boosts inquiries 4× (max 100 MB, up to 3 clips).
                </p>
                {uploadingVideo && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                    <Loader2 size={14} className="animate-spin" /> Uploading video — this can take a minute…
                  </div>
                )}
                {videos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-2" data-testid="add-prop-video-thumbs">
                    {videos.map((v, i) => (
                      <div key={v || `vid-${i}`} className="relative group border border-slate-200 rounded-md overflow-hidden bg-slate-900">
                        {/* Silent thumbnail preview — muted, no controls */}
                        <video
                          src={v.match(/^\/api\/(uploads|files)\//) ? `${BACKEND_URL}${v}` : v}
                          className="w-full h-20 object-cover"
                          preload="metadata"
                          muted
                        >
                          <track kind="captions" />
                        </video>
                        <button
                          type="button"
                          onClick={() => setVideos((p) => p.filter((_, idx) => idx !== i))}
                          className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`Remove video ${i + 1}`}
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.has_documents}
                  onChange={(e) => setForm({ ...form, has_documents: e.target.checked })}
                  className="w-4 h-4 accent-emerald-700"
                  data-testid="add-prop-documents"
                />
                I have valid property documents (title deed / encumbrance / RTC)
              </label>
              <div>
                <Label>Additional notes (optional)</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Best time to call, negotiability, etc."
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
              Cancel
            </Button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-am-primary flex-1 py-3 text-base"
              data-testid="add-prop-submit"
            >
              {submitting ? 'Submitting…' : 'Submit Property'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddPropertyModal;
